import { GetPostQuery } from "@/__generated__/graphql";
import className from "classnames/bind";

import styles from "./CantUnhearItTrack.module.scss";
import { Image } from "@/components/Image";
import {
  forwardRef,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useViewSize } from "@/providers/ViewSizeContext";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useHeader } from "@/providers/HeaderContext";
import { dedupeLineBreaks } from "@/core/utils";
import debounce from "lodash.debounce";
import { useClientMount } from "@/providers/ClientMountProvider";

const cx = className.bind(styles);
type CantUnhearItTrackProps = {
  track: GetPostQuery["rhPost"]["featureData"]["sonicConnections"]["tracks"][number];
  last: boolean;
  first: boolean;
};

export interface CantUnhearItTrackRefs {
  getRootEl: () => HTMLDivElement | null;
  getTrigger: () => HTMLElement | null;
  getLastEl: () => HTMLElement | null;
}

const CantUnhearItTrack = forwardRef<
  CantUnhearItTrackRefs,
  CantUnhearItTrackProps
>(({ track, last, first }, ref) => {
  const containerEl = useRef<HTMLDivElement>(null);
  const triggerEl = useRef<HTMLElement>(null);
  const lastEl = useRef<HTMLElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isTint, setTint] = useState(false);
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const imagesList = useRef<Array<HTMLImageElement | null>>([]);

  const artistName = dedupeLineBreaks(track.artistName);
  const trackName = dedupeLineBreaks(track.trackName);

  const {
    isScrolled: isHeaderScrolled,
    scrollDirection: headerScrollDirection,
  } = useHeader();
  const mobileHeaderEl = useRef<HTMLDivElement>(null);
  const total = useMemo(() => {
    return Math.max(track.images.length, track.blurbs.length);
  }, [track.images, track.blurbs]);

  const setRefs = useCallback((el: HTMLElement | null, index: number) => {
    if (el) {
      // Only run logic when element exists
      if (!index && triggerEl.current !== el) {
        triggerEl.current = el;
      } else if (index === total - 1 && lastEl.current !== el) {
        lastEl.current = el;
      }
    }
  }, []);

  useImperativeHandle(ref, () => ({
    getRootEl: () => containerEl.current,
    getTrigger: () => triggerEl.current,
    getLastEl: () => lastEl.current,
  }));

  useEffect(() => {
    if (!mounted || !isReady) return;

    if (isHeaderSticky) {
      gsap.set(mobileHeaderEl.current, {
        position: "fixed",
        zIndex: 14,
      });
      gsap.set(containerEl.current, { paddingTop: "24vw" });
    } else {
      if (mobileHeaderEl.current) {
        gsap.set(mobileHeaderEl.current, { position: "relative" });
      }
      gsap.set(containerEl.current, { paddingTop: "unset" });
    }
  }, [mounted, isReady, isHeaderSticky]);

  useEffect(() => {
    // Early return for desktop or SSR - no ScrollTrigger needed
    if (!mounted || !isReady || isDesktop || !containerEl.current) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const trigger = ScrollTrigger.create({
      trigger: containerEl.current,
      start: "top top",
      end: "bottom top",
      onEnter: () => {
        setIsHeaderSticky(true);
      },
      onLeave: () => {
        setIsHeaderSticky(false);
      },
      onLeaveBack: () => {
        setIsHeaderSticky(false);
      },
      onEnterBack: () => {
        setIsHeaderSticky(true);
      },
      onUpdate: (self) => {
        if (last) return;

        const threshhold = first ? 0.85 : 0.75;

        if (self.progress > threshhold) {
          setTint(true);
        } else {
          setTint(false);
        }
      },
    });

    const handleScroll = () => {
      ScrollTrigger.refresh(true);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      trigger.kill();
    };
  }, [mounted, isReady, isDesktop, last, first, containerEl.current]);

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      ScrollTrigger.refresh();
    },
    [track.images.length]
  );

  return (
    <div
      className={cx("track", { "header-sticky": isHeaderSticky })}
      ref={containerEl}
    >
      <div
        className={cx("track-mobile-header", {
          tint: isTint,
          sticky: isHeaderSticky,
          "is-scrolled":
            isHeaderSticky &&
            // scrollCoef > 0.05 &&
            isHeaderScrolled &&
            headerScrollDirection === "up",
        })}
        ref={mobileHeaderEl}
      >
        <div className={cx("track-album")}>
          <Image image={track.heroImage} />
          <div
            className={cx("disc-image", { animate: isHeaderSticky || first })}
          >
            <Image image={track.heroImage} />
          </div>
        </div>
        <div className={cx("track-mobile-header-text")}>
          <h3
            className={cx("track-name")}
            dangerouslySetInnerHTML={{ __html: trackName }}
          />
          <p
            className={cx("artist-name")}
            dangerouslySetInnerHTML={{ __html: artistName }}
          />
          <p className={cx("release-year")}>{track.releaseYear}</p>
        </div>
      </div>
      <div className={cx("track-images-and-blurbs")}>
        {new Array(total).fill(null).map((item, index) => (
          <div
            className={cx("track-detail-group-container")}
            key={`detail-group-${index}`}
          >
            <div
              ref={(el) => setRefs(el, index)}
              className={cx("track-detail-group", {
                "track-detail-group-reverse":
                  (index % 2 !== 0 && !track.flipContentOrder) ||
                  (track.flipContentOrder && index % 2 === 0),
              })}
            >
              {track.images[index] && (
                <Image
                  className={cx("track-image")}
                  image={track.images[index].image}
                  onLoad={handleImageLoad}
                  ref={(image) => {
                    imagesList.current[index] = image;
                  }}
                />
              )}
              {track.blurbs[index] && (
                <div className={cx("blurb")}>
                  <span className={cx("eyebrow", "eyebrow-s")}>
                    {track.blurbs[index].eyebrow}
                  </span>
                  <p className={cx("copy", "text-m")}>
                    {track.blurbs[index].copy}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default CantUnhearItTrack;
